import { isCommodity, type Commodity } from '@enmon/commons/lib/shared';
import type { AvailableCommoditiesShape } from '@/containers/AuthenticatedApp/selectors';
import { pick } from 'ramda';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import styled from 'styled-components';
import { COMMODITY_TRANSLATED } from '@/common/messages';
import { Tooltip } from '@/components/tooltip/Tooltip';
import { Col, Row } from 'antd';
import { Icon } from '@/components/icon';
import { sortCommodities } from '@/utils/sort/sortCommodities';
import { THEME } from '@enmon/commons';
import { Typography } from '@/components/typography';
import { useAvailableCommoditiesConfig } from '@/containers/App';

type Option = {
  value: Commodity;
  label: ReactNode;
};

export type CommodityOptionLayout = 'icon' | 'row' | 'tile';

const OptionWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InlineOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const useCommodityOptions = (
  allowed: Commodity[] | null,
  onlyConsumption: boolean,
  checkedValue?: Commodity[],
  layout: CommodityOptionLayout = 'icon',
): Option[] => {
  const availableCommodities: AvailableCommoditiesShape = useAvailableCommoditiesConfig({ onlyConsumption });

  const commodities = allowed ? pick(allowed, availableCommodities) : availableCommodities;
  const intl = useIntl();

  const options: Option[] = Object.keys(commodities)
    .filter(isCommodity)
    .sort(sortCommodities)
    .map<Option>(commodityKey => ({
      value: commodityKey,
      label:
        layout === 'row' ? (
          <InlineOption
            data-cy={`commodity-option-${commodityKey}${(checkedValue ?? []).includes(commodityKey) ? '-checked' : ''}`}
          >
            {commodities[commodityKey]?.iconString ? (
              <Icon
                aria-label={intl.formatMessage(COMMODITY_TRANSLATED[commodityKey])}
                iconString={commodities[commodityKey]?.iconString}
              />
            ) : undefined}
            <FormattedMessage {...commodities[commodityKey]?.message} />
          </InlineOption>
        ) : layout === 'icon' ? (
          <Tooltip title={<FormattedMessage {...commodities[commodityKey]?.message} />}>
            <OptionWrapper
              aria-label={intl.formatMessage(COMMODITY_TRANSLATED[commodityKey])}
              data-cy={`commodity-option-${commodityKey}${(checkedValue ?? []).includes(commodityKey) ? '-checked' : ''}`}
            >
              {commodities[commodityKey]?.iconString ? (
                <Icon iconString={commodities[commodityKey]?.iconString} />
              ) : undefined}
            </OptionWrapper>
          </Tooltip>
        ) : (
          <OptionWrapper
            data-cy={`commodity-option-${commodityKey}${(checkedValue ?? []).includes(commodityKey) ? '-checked' : ''}`}
          >
            <Row align="middle" justify="center">
              <Col span={24}>
                {commodities[commodityKey]?.iconString ? (
                  <Icon
                    $fontSize={THEME.typography.fontSize[5]}
                    aria-label={intl.formatMessage(COMMODITY_TRANSLATED[commodityKey])}
                    iconString={commodities[commodityKey]?.iconString}
                  />
                ) : undefined}
              </Col>
              <Col span={24}>
                <Typography.Paragraph>
                  <FormattedMessage {...commodities[commodityKey]?.message} />
                </Typography.Paragraph>
              </Col>
            </Row>
          </OptionWrapper>
        ),
    }));

  return options;
};
